import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { streamsApi } from '@api/streams';
import { HttpError } from '@api/http';
import { resetVideoElement, startHlsPlayback, type HlsPlaybackSession } from '@lib/hls-player';
import type { CameraStatus } from '@api/types';

type Phase = 'idle' | 'loading' | 'playing' | 'offline' | 'error';

interface Props {
  cameraId: number;
  status: CameraStatus;
}

export function LivePreview({ cameraId, status }: Props): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (status !== 'active') {
      setPhase('offline');
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    let session: HlsPlaybackSession | null = null;
    const video = videoRef.current;

    setPhase('loading');
    setError(null);

    if (!video) return undefined;

    resetVideoElement(video);

    streamsApi
      .getDescriptor(cameraId, controller.signal)
      .then(async (descriptor) => {
        if (cancelled || !videoRef.current) return;
        if (descriptor.mediamtxStatus !== 'registered') {
          throw new Error(descriptor.mediamtxError ?? 'Stream not registered with MediaMTX');
        }

        session = await startHlsPlayback({
          video: videoRef.current,
          sourceUrl: descriptor.hlsUrl,
          signal: controller.signal,
          lowLatencyMode: true,
          fatalErrorMessage: 'Failed to start stream',
          onReady: () => {
            if (cancelled) return;
            void videoRef.current?.play().catch(() => undefined);
            setPhase('playing');
          },
          onFatalError: (message) => {
            if (cancelled) return;
            setError(message);
            setPhase('error');
          },
        });
      })
      .catch((err: unknown) => {
        if (cancelled || axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to start stream';
        setError(message);
        setPhase('error');
      });

    return () => {
      cancelled = true;
      controller.abort();
      session?.destroy();
      resetVideoElement(video);
    };
  }, [cameraId, status, retryToken]);

  return (
    <div className="absolute inset-0">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className={clsx(
          'h-full w-full bg-black object-contain transition-opacity',
          phase === 'playing' ? 'opacity-100' : 'opacity-0',
        )}
      />

      {phase !== 'playing' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-bg/40 text-center">
          <div className="pointer-events-auto flex flex-col items-center gap-2 px-4">
            {phase === 'loading' && (
              <span className="text-xs uppercase tracking-wider text-muted">Connecting…</span>
            )}
            {phase === 'offline' && (
              <span className="text-xs uppercase tracking-wider text-muted">Camera offline</span>
            )}
            {phase === 'error' && (
              <>
                <span className="text-xs font-medium text-danger">Stream unavailable</span>
                {error && <span className="line-clamp-2 text-xs text-muted">{error}</span>}
                <button
                  type="button"
                  onClick={() => setRetryToken((n) => n + 1)}
                  className={clsx(
                    'rounded border border-border bg-surface px-2 py-0.5 text-[11px] text-foreground',
                    'hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  )}
                >
                  Retry
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
